import React, { useMemo } from 'react'
import { FieldValues, Path, UseFormReturn } from 'react-hook-form'
import { Spinner } from '@components/Spinner'
import { TEMPORARY_SPACE_ICON_URL } from '@components/Web3/constants'
import { Box, BoxProps, ErrorMessage, Icon, IconProps, Text } from '@ui'
import * as fieldStyles from 'ui/components/_internal/Field/Field.css'
import { FieldOutline } from 'ui/components/_internal/Field/FieldOutline/FieldOutline'
import { UploadInput } from 'ui/components/Form/Upload'
import { srOnlyClass } from 'ui/styles/globals/utils.css'
import { vars } from 'ui/styles/vars.css'
import { useImageStore } from 'use-towns-client'

import { loadingStyles, spinnerStyles } from './UploadImage.css'
import { useOnImageChangeEvent, UseOnImageChangeEventProps } from './useOnImageChangeEvent'

export type UploadImageTemplateSize = 'tabletToDesktop' | 'lg' | 'md' | 'sm'

type Props<T extends FieldValues> = {
    children: React.ReactNode
    canEdit: boolean
    resourceId: string
    formFieldName: Path<T>
    type: UseOnImageChangeEventProps<T>['type']
    imageRestrictions?: UseOnImageChangeEventProps<T>['imageRestrictions']
    onUploadImage?: UseOnImageChangeEventProps<T>['onUploadImage']
    uploadIconPosition?: BoxProps['position']
    uploadIconSize?: IconProps['size']
    size?: UploadImageTemplateSize
    dataTestId?: string
} & Pick<UseFormReturn<T>, 'setError' | 'clearErrors' | 'formState' | 'register'>

const config: {
    [key in UploadImageTemplateSize]: BoxProps
} = {
    tabletToDesktop: {
        width: {
            tablet: '100',
            desktop: '200',
        },
        height: {
            tablet: '100',
            desktop: '200',
        },
        rounded: 'md',
    },
    sm: {
        width: 'x10',
        height: 'x10',
        rounded: 'md',
    },
    md: {
        width: '150',
        height: '150',
        rounded: 'md',
    },
    lg: {
        width: '200',
        height: '200',
        rounded: 'md',
    },
}

export const LargeUploadImageTemplate = <T extends FieldValues>(props: Props<T>) => {
    const {
        children,
        canEdit,
        formFieldName,
        type,
        resourceId,
        imageRestrictions,
        register,
        formState,
        setError,
        clearErrors,
        onUploadImage,
        uploadIconSize = 'square_sm',
        uploadIconPosition = 'topRight',
        size = 'lg',
        dataTestId,
    } = props

    const { onChange, isUploading } = useOnImageChangeEvent({
        onUploadImage,
        resourceId,
        type,
        imageRestrictions,
        formFieldName,
        setError,
        clearErrors,
        formState,
    })

    const failedResource = useImageStore((state) => state.erroredResources[resourceId])
    const loadedResource = useImageStore((state) => state.loadedResource[resourceId])
    const isLoading = resourceId !== TEMPORARY_SPACE_ICON_URL && !failedResource && !loadedResource

    const isLoadingOrUploading = isLoading || isUploading

    const spinnerText = useMemo(() => {
        if (isUploading) {
            return 'Uploading Image'
        } else if (isLoading) {
            return 'Getting Image'
        }
        return ''
    }, [isLoading, isUploading])

    const ref = React.useRef<HTMLInputElement>(null)

    function onClick() {
        if (isLoadingOrUploading) {
            return
        }
        ref.current?.click()
    }

    const noImage = uploadIconPosition === 'absoluteCenter'

    return (
        <Box position="relative" data-testid="upload-image-container">
            <Box className={isLoadingOrUploading ? loadingStyles : ''}>{children}</Box>

            {isLoadingOrUploading && (
                <>
                    <Box className={spinnerStyles}>
                        <Spinner />
                        <Box>
                            <Text size="sm">{spinnerText}</Text>
                        </Box>
                    </Box>
                </>
            )}

            {canEdit && (
                <>
                    <Box
                        {...config[size]}
                        centerContent
                        position="absoluteCenter"
                        background={noImage ? 'level2' : 'none'}
                        justifySelf="center"
                        alignSelf="center"
                        cursor={noImage ? 'pointer' : 'default'}
                        pointerEvents={noImage ? 'all' : 'none'}
                        onClick={noImage ? onClick : undefined}
                    >
                        <UploadInput
                            name={formFieldName}
                            className={[fieldStyles.field, srOnlyClass]}
                            ref={ref}
                            register={register}
                            accept="image/*"
                            dataTestId={dataTestId}
                            onChange={onChange}
                        />
                        <FieldOutline tone="accent" rounded="md" />
                    </Box>
                    <Box position={uploadIconPosition} padding="md" pointerEvents="none">
                        <Box
                            centerContent
                            disabled={isLoadingOrUploading}
                            data-testid="upload-image-button"
                            top="xs"
                            right="xs"
                            background={noImage ? 'level2' : 'level3'}
                            width="x4"
                            height="x4"
                            rounded="full"
                            border={noImage ? 'none' : 'strongLevel1'}
                            padding="md"
                            cursor="pointer"
                            pointerEvents={noImage ? 'none' : 'all'}
                            onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                onClick()
                            }}
                        >
                            <Icon
                                type="camera"
                                size={uploadIconSize}
                                style={{
                                    color: vars.color.foreground.gray2,
                                }}
                            />
                        </Box>
                    </Box>
                    {formState?.errors[formFieldName] &&
                        !(
                            typeof formState.errors[formFieldName]?.message === 'string' &&
                            formState.errors[formFieldName]?.message?.includes(
                                'minimum height & width',
                            )
                        ) && (
                            <Box
                                centerContent
                                padding
                                width="100%"
                                position="absolute"
                                bottom="-x8"
                            >
                                <ErrorMessage
                                    maxWidth="200"
                                    textProps={{
                                        textAlign: 'center',
                                    }}
                                    errors={formState.errors}
                                    fieldName={formFieldName}
                                />
                            </Box>
                        )}
                </>
            )}
        </Box>
    )
}
